import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AccountHeader from './components/AccountHeader';
import ProfileOverview from './components/ProfileOverview';
import ProfileEditor from './components/ProfileEditor';
import MyCourses from './components/MyCourses';
import MyBookings from './components/MyBookings';
import BookingPaymentModal from './components/BookingPaymentModal';
import MembershipOrders from './components/MembershipOrders';
import OrderHistory from './components/OrderHistory';
import OrderPaymentModal from './components/OrderPaymentModal';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { adaptMembershipOrder, adaptMentorBooking, adaptMyCourse, adaptOrder } from '@/lib/adapters';
import MembershipPaymentModal from '@/components/payment/MembershipPaymentModal';

type TabType = 'profile' | 'profile-edit' | 'courses' | 'bookings' | 'orders' | 'memberships';

const AccountPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'courses';
  const tabSet: TabType[] = ['profile', 'profile-edit', 'courses', 'bookings', 'orders', 'memberships'];
  const [activeTab, setActiveTab] = useState<TabType>(tabSet.includes(initialTab) ? initialTab : 'courses');
  const [courses, setCourses] = useState<ReturnType<typeof adaptMyCourse>[]>([]);
  const [bookings, setBookings] = useState<ReturnType<typeof adaptMentorBooking>[]>([]);
  const [orders, setOrders] = useState<ReturnType<typeof adaptOrder>[]>([]);
  const [membershipOrders, setMembershipOrders] = useState<ReturnType<typeof adaptMembershipOrder>[]>([]);
  const [activeBookingPaymentId, setActiveBookingPaymentId] = useState<string | null>(null);
  const [activeMembershipPaymentId, setActiveMembershipPaymentId] = useState<string | null>(null);
  const [activeOrderPaymentId, setActiveOrderPaymentId] = useState<string | null>(null);
  const { user, refreshUser } = useAuth();

  const loadAccountData = () => {
    void Promise.all([
      api.getMyCourses({ pageCurrent: 1, pageSize: 20 }),
      api.getMyBookings({ pageCurrent: 1, pageSize: 20 }),
      api.getMyOrders({ pageCurrent: 1, pageSize: 20 }),
      api.getMyMembershipOrders({ pageCurrent: 1, pageSize: 20 })
    ]).then(([coursePage, bookingPage, orderPage, membershipOrderPage]) => {
      setCourses(coursePage.list.map(adaptMyCourse));
      setBookings(bookingPage.list.map(adaptMentorBooking));
      setOrders(orderPage.list.map(adaptOrder));
      setMembershipOrders(membershipOrderPage.list.map(adaptMembershipOrder));
    });
  };

  useEffect(() => {
    loadAccountData();
  }, []);

  const handleCancelBooking = (id: string) => {
    void api.cancelMentorBooking({ id }).then(() => {
      setBookings((current) =>
        current.map((item) =>
          item.id === id
            ? { ...item, status: 'closed', bookingStatus: 4, bookingStatusText: '已取消' }
            : item
        )
      );
    });
  };

  const handleContinueBookingPay = (id: string) => {
    setActiveBookingPaymentId(id);
  };

  const bookingStatusTextByCode = (status: number) => {
    if (status === 5) return '已完成';
    if (status === 4) return '已取消';
    if (status === 3) return '已確認';
    if (status === 2) return '待確認';
    return '待選時間';
  };

  const handleSetBookingSchedule = async ({ id, bookingDate, bookingTime }: { id: string; bookingDate: string; bookingTime: string }) => {
    await api.updateMentorBookingTime({
      id,
      bookingDate,
      bookingTime,
      bookingTimezone: 'HKT',
      preferTime: `${bookingDate} ${bookingTime}`
    });
    setBookings((current) =>
      current.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const nextStatus = item.bookingStatus === 1 ? 2 : item.bookingStatus;
        return {
          ...item,
          bookingDate,
          bookingTime,
          bookingTimezone: 'HKT',
          preferTime: `${bookingDate} ${bookingTime}`,
          bookingStatus: nextStatus,
          bookingStatusText: bookingStatusTextByCode(nextStatus)
        };
      })
    );
  };

  const activeBooking = bookings.find((item) => item.id === activeBookingPaymentId) || null;
  const activeMembershipOrder = membershipOrders.find((item) => item.id === activeMembershipPaymentId) || null;
  const activeOrder = orders.find((item) => item.id === activeOrderPaymentId) || null;

  const tabs: { id: TabType; label: string; icon: string; count?: number }[] = [
    { id: 'profile', label: '個人信息', icon: 'ri-user-3-line' },
    { id: 'profile-edit', label: '編輯資料', icon: 'ri-edit-box-line' },
    { id: 'courses', label: '我的課程', icon: 'ri-book-open-line', count: courses.length },
    { id: 'bookings', label: '我的預約', icon: 'ri-calendar-check-line', count: bookings.length },
    { id: 'orders', label: '訂單記錄', icon: 'ri-receipt-line', count: orders.length },
    { id: 'memberships', label: '會員訂單', icon: 'ri-vip-crown-line', count: membershipOrders.length }
  ];

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f7fafc_0%,_#f4f7ff_38%,_#f8fafc_100%)] font-sans">
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <AccountHeader
          userName={user?.nickname || user?.email || 'NexusGo 用戶'}
          email={user?.email || ''}
          mobile={user?.mobile}
          avatar={user?.userHead}
          availableAmount={user?.availableAmount}
          coursesCount={courses.length}
          ordersCount={orders.length}
          member={user?.member}
          memberPlanName={user?.memberPlanName}
          memberExpireTime={user?.memberExpireTime}
        />

        <div className="bg-white/88 backdrop-blur border-b border-slate-100 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold cursor-pointer transition-all whitespace-nowrap border border-transparent rounded-xl ${
                    activeTab === tab.id
                      ? 'text-sky-700 bg-sky-50 border-sky-100'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-200'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                  {tab.count !== undefined ? (
                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.id ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {tab.count}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          {activeTab === 'profile' && <ProfileOverview user={user} />}
          {activeTab === 'profile-edit' && (
            <ProfileEditor
              user={user}
              onSaved={() => {
                void refreshUser();
              }}
            />
          )}
          {activeTab === 'courses' && <MyCourses courses={courses} />}
          {activeTab === 'bookings' && <MyBookings bookings={bookings} onCancel={handleCancelBooking} onContinuePay={handleContinueBookingPay} onSetSchedule={handleSetBookingSchedule} />}
          {activeTab === 'orders' && <OrderHistory orders={orders} onContinuePay={(id) => setActiveOrderPaymentId(id)} />}
          {activeTab === 'memberships' && (
            <MembershipOrders
              orders={membershipOrders}
              onContinuePay={(id) => setActiveMembershipPaymentId(id)}
            />
          )}
        </div>
      </div>
      {activeBooking ? (
        <BookingPaymentModal
          booking={activeBooking}
          onClose={() => setActiveBookingPaymentId(null)}
          onSuccess={({ bookingId, orderStatus, payTime }) => {
            setBookings((current) =>
              current.map((item) =>
                item.id === bookingId
                  ? {
                      ...item,
                      status: orderStatus === 2 ? 'paid' : item.status,
                      date: payTime || item.date,
                      payTime: payTime || item.payTime
                    }
                  : item
              )
            );
            setActiveBookingPaymentId(null);
          }}
        />
      ) : null}
      {activeMembershipOrder ? (
        <MembershipPaymentModal
          target={{
            mode: 'continue',
            orderNo: activeMembershipOrder.orderId,
            planName: activeMembershipOrder.title,
            planPrice: activeMembershipOrder.amount,
            originalPrice: activeMembershipOrder.amount
          }}
          onClose={() => setActiveMembershipPaymentId(null)}
          onSuccess={() => {
            void api.getMyMembershipOrders({ pageCurrent: 1, pageSize: 20 }).then((page) => {
              setMembershipOrders(page.list.map(adaptMembershipOrder));
            });
            void refreshUser();
            setActiveMembershipPaymentId(null);
          }}
        />
      ) : null}
      {activeOrder ? (
        <OrderPaymentModal
          order={activeOrder}
          onClose={() => setActiveOrderPaymentId(null)}
          onSuccess={({ orderId, orderStatus, payTime }) => {
            setOrders((current) =>
              current.map((item) =>
                item.id === orderId
                  ? {
                      ...item,
                      status: orderStatus === 2 ? 'paid' : item.status,
                      date: payTime || item.date
                    }
                  : item
              )
            );
            setActiveOrderPaymentId(null);
          }}
        />
      ) : null}
      <Footer />
    </div>
  );
};

export default AccountPage;
